import _ from "lodash";
import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { Modal, Checkbox, Alert, Button, Tooltip } from "@toddle-edu/ds-web";
import { FormTextAreaInput } from "UIComponents/TextAreaInput";
import { Trans } from "react-i18next";
import { useI18n } from "Hooks";
import { getIsEnabledRecycleBinForAssignment } from "RecycleBin/modules/Utils";
import classes from "./AssignmentActionDialog.scss";
import {
  isGlobalHeaderOnMultipleScreensEnabled,
  getStyleStrippedText,
} from "Utils";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";

const DIALOG_ACTIONS_WITH_ALERT = ["EVALUATE", "EVALUATE_SHARE"];

const styles = {
  listContainerStyle: {
    paddingLeft: "24px",
  },
  alertContainerStyle: {
    marginBottom: "8px",
  },
};

const getDialogAlertText = ({
  t,
  dialogAction,
  isStudentSubmissionEnabled,
  notSubmittedCount,
  draftSubmissionsCount,
  isPrivate,
}) => {
  if (
    _.includes(DIALOG_ACTIONS_WITH_ALERT, dialogAction) &&
    isStudentSubmissionEnabled &&
    notSubmittedCount > 0 &&
    !isPrivate
  ) {
    const finalNotSubmittedCount = notSubmittedCount - draftSubmissionsCount;

    if (finalNotSubmittedCount > 0 && draftSubmissionsCount > 0) {
      return t("common:entity1_and_entity2", {
        entity1: t("assessmentEvaluation:not_submitted_alert_message", {
          count: finalNotSubmittedCount,
        }).slice(0, -1),
        entity2: t(
          "assessmentEvaluation:work_not_marked_as_done_alert_message",
          {
            count: draftSubmissionsCount,
          }
        ),
      });
    } else if (finalNotSubmittedCount > 0) {
      return t("assessmentEvaluation:not_submitted_alert_message", {
        count: finalNotSubmittedCount,
      });
    } else if (draftSubmissionsCount > 0) {
      return t("assessmentEvaluation:work_not_marked_as_done_alert_message", {
        count: draftSubmissionsCount,
      });
    }
  }

  return null;
};

const getSingleStudentActionDialogInfo = ({
  t,
  dialogAction,
  studentName,
  isTeacherEvaluationEnabled,
  isDraftSubmission,
  isSubmitted,
  allowMovingTaskToReviewed = false,
  notEvaluatedCount,
  showCloseSubmissionCheckbox,
  isSubmissionLocked,
}) => {
  let title = null;
  let message = null;
  const inputFields = [];
  let confirmButtonText = null;
  let alertText = null;

  const showCloseSubmissionCheckboxWithMsg =
    showCloseSubmissionCheckbox &&
    !isSubmissionLocked &&
    isTeacherEvaluationEnabled;

  switch (dialogAction) {
    case "EVALUATE": {
      const action = isTeacherEvaluationEnabled
        ? t("classRoom:evaluated")
        : t("classRoom:completed");

      title = t("classRoom:mark_name_as_action", {
        name: studentName,
        action,
      });

      let messageSuffix = "";

      message =
        showCloseSubmissionCheckboxWithMsg &&
        notEvaluatedCount === 1 &&
        allowMovingTaskToReviewed
          ? t(
              "assessmentEvaluation:mark_student_as_evaluated_message_with_close_submission_and_reviewed",
              {
                name: studentName,
                label: action,
              }
            )
          : showCloseSubmissionCheckboxWithMsg
          ? t(
              "assessmentEvaluation:mark_student_as_evaluated_message_with_close_submission",
              {
                name: studentName,
                label: action,
              }
            )
          : t("assessmentEvaluation:mark_student_as_evaluated_message", {
              name: studentName,
              label: action,
            });

      if (isDraftSubmission) {
        alertText = t(
          "assessmentEvaluation:work_not_marked_as_done_by_student_alert_message",
          {
            name: studentName,
          }
        );
      } else if (!isSubmitted) {
        alertText = t(
          "assessmentEvaluation:work_not_submitted_by_student_alert_message",
          {
            name: studentName,
          }
        );
      }

      if (notEvaluatedCount === 1 && allowMovingTaskToReviewed) {
        messageSuffix = t("classRoom:should_move_task_to_reviewed_tab");
        inputFields.push({
          key: "moveToReviewed",
          label: t("classRoom:move_task_to_reviewed_tab"),
          uiType: "CHECKBOX",
        });
      }

      if (!_.isEmpty(messageSuffix) && !showCloseSubmissionCheckboxWithMsg) {
        message = `${message} ${messageSuffix}`;
      }

      confirmButtonText = isTeacherEvaluationEnabled
        ? t("assessmentEvaluation:mark_as_evaluated")
        : t("assessmentEvaluation:mark_as_completed");

      if (showCloseSubmissionCheckboxWithMsg) {
        inputFields.push({
          key: "closeSubmissions",
          label: t("assessmentEvaluation:close_submission_checkbox_text", {
            name: studentName,
          }),
          uiType: "CHECKBOX",
        });
      }
      break;
    }
    case "EVALUATE_SHARE": {
      const action = t("common:entity1_and_entity2", {
        entity1: t("classRoom:evaluated"),
        entity2: t("common:share"),
      });

      title = t("classRoom:mark_name_as_action", {
        name: studentName,
        action,
      });

      let messageSuffix = "";

      message =
        showCloseSubmissionCheckboxWithMsg &&
        notEvaluatedCount === 1 &&
        allowMovingTaskToReviewed
          ? t(
              "assessmentEvaluation:mark_student_as_evaluated_and_share_message_with_close_submission_and_reviewed",
              {
                name: studentName,
              }
            )
          : showCloseSubmissionCheckboxWithMsg
          ? t(
              "assessmentEvaluation:mark_student_as_evaluated_and_share_message_with_close_submission",
              {
                name: studentName,
              }
            )
          : t(
              "assessmentEvaluation:mark_student_as_evaluated_and_share_message",
              {
                name: studentName,
                label: action,
              }
            );

      if (isDraftSubmission) {
        alertText = t(
          "assessmentEvaluation:work_not_marked_as_done_by_student_alert_message",
          {
            name: studentName,
          }
        );
      } else if (!isSubmitted) {
        alertText = t(
          "assessmentEvaluation:work_not_submitted_by_student_alert_message",
          {
            name: studentName,
          }
        );
      }

      if (notEvaluatedCount === 1 && allowMovingTaskToReviewed) {
        messageSuffix = t("classRoom:should_move_task_to_reviewed_tab");
        inputFields.push({
          key: "moveToReviewed",
          label: t("classRoom:move_task_to_reviewed_tab"),
          uiType: "CHECKBOX",
        });
      }

      if (!_.isEmpty(messageSuffix) && !showCloseSubmissionCheckboxWithMsg) {
        message = `${message} ${messageSuffix}`;
      }

      confirmButtonText = showCloseSubmissionCheckboxWithMsg
        ? t("common:mark_and_share")
        : t("common:mark_as_label", {
            label: action,
          });

      if (showCloseSubmissionCheckboxWithMsg) {
        inputFields.push({
          key: "closeSubmissions",
          label: t("assessmentEvaluation:close_submission_checkbox_text", {
            name: studentName,
          }),
          uiType: "CHECKBOX",
        });
      }

      break;
    }

    case "SHARE": {
      title = t("classRoom:share_eval_with_name", { name: studentName });

      if (isDraftSubmission) {
        message = showCloseSubmissionCheckboxWithMsg
          ? t("assessmentEvaluation:share_evaluation_for_single_student_body", {
              name: studentName,
            })
          : t(
              "classRoom:share_eval_submitted_but_not_marked_as_done_with_name_body",
              {
                name: studentName,
              }
            );
      } else if (!isSubmitted) {
        message = showCloseSubmissionCheckboxWithMsg
          ? t("assessmentEvaluation:share_evaluation_for_single_student_body", {
              name: studentName,
            })
          : t("classRoom:share_eval_with_name_body", {
              name: studentName,
            });
      }

      confirmButtonText = t("common:share_with_label", {
        label: t("common:evaluation"),
      });

      if (showCloseSubmissionCheckboxWithMsg) {
        inputFields.push({
          key: "closeSubmissions",
          label: t("assessmentEvaluation:close_submission_checkbox_text", {
            name: studentName,
          }),
          uiType: "CHECKBOX",
        });
      }

      break;
    }

    case "RETURN": {
      title = t("common:return_submission_label", { name: studentName });
      message = t("common:remark");
      inputFields.push({
        key: "remark",
        label: t("common:enter_remark_here"),
        uiType: "TEXTAREA",
      });
      confirmButtonText = t("common:return");
    }
  }

  return {
    title,
    message,
    inputFields,
    confirmButtonText,
    alertText,
  };
};

const getSelectedStudentsActionDialogInfo = ({
  t,
  dialogAction,
  isTeacherEvaluationEnabled,
  isStudentSubmissionEnabled,
  isPrivate,
  selectedStudentsCount,
  notSubmittedCount,
  draftSubmissionsCount,
  notSharedWithStudentsCount,
  totalNotEvaluatedStudents,
  selectedStudents,
  allowMovingTaskToReviewed = false,
  showCloseSubmissionCheckbox,
  totalStudents,
  isSubmissionLocked,
}) => {
  let title = null;
  let message = null;
  const inputFields = [];
  let confirmButtonText = null;
  const allNonEvaluatedStudentsSelected = _.every(
    totalNotEvaluatedStudents,
    id => _.includes(selectedStudents, id)
  );

  const showCloseSubmissionCheckboxWithMsg =
    showCloseSubmissionCheckbox &&
    !isSubmissionLocked &&
    isTeacherEvaluationEnabled;

  switch (dialogAction) {
    case "EVALUATE": {
      const label = isTeacherEvaluationEnabled
        ? t("classRoom:evaluated")
        : t("classRoom:completed");

      title = t("classRoom:mark_count_student_as_label", {
        label,
        count: selectedStudentsCount,
      });

      let messageSuffix = "";

      message =
        showCloseSubmissionCheckboxWithMsg &&
        allNonEvaluatedStudentsSelected &&
        allowMovingTaskToReviewed
          ? t(
              "assessmentEvaluation:mark_count_student_evaluated_with_close_submission_and_reviewed",
              {
                count: selectedStudentsCount,
                totalCount: totalStudents,
                label,
              }
            )
          : showCloseSubmissionCheckboxWithMsg
          ? t(
              "assessmentEvaluation:mark_count_student_evaluated_with_close_submission",
              {
                count: selectedStudentsCount,
                totalCount: totalStudents,
                label,
              }
            )
          : t("assessmentEvaluation:mark_count_student_evaluated_message_v2", {
              label,
              count: selectedStudentsCount,
            });

      confirmButtonText = t("common:mark_as_label", { label });

      if (allNonEvaluatedStudentsSelected && allowMovingTaskToReviewed) {
        messageSuffix = t("classRoom:should_move_task_to_reviewed_tab");
        inputFields.push({
          key: "moveToReviewed",
          label: t("classRoom:move_task_to_reviewed_tab"),
          uiType: "CHECKBOX",
        });
      }

      if (!_.isEmpty(messageSuffix) && !showCloseSubmissionCheckboxWithMsg) {
        message = `${message} ${messageSuffix}`;
      }

      if (showCloseSubmissionCheckboxWithMsg) {
        inputFields.push({
          key: "closeSubmissions",
          label: t(
            "assessmentEvaluation:close_submission_selected_student_action",
            {
              count: selectedStudentsCount,
            }
          ),
          uiType: "CHECKBOX",
        });
      }

      break;
    }

    case "EVALUATE_SHARE": {
      const label = t("common:entity1_and_entity2", {
        entity1: t("classRoom:evaluated"),
        entity2: t("common:share"),
      });

      title = t("classRoom:mark_count_student_as_label", {
        label,
        count: selectedStudentsCount,
      });

      let messageSuffix = "";

      message =
        showCloseSubmissionCheckboxWithMsg &&
        allNonEvaluatedStudentsSelected &&
        allowMovingTaskToReviewed
          ? t(
              "assessmentEvaluation:mark_count_student_evaluated_and_share_with_close_submission_and_reviewed",
              {
                count: selectedStudentsCount,
                totalCount: totalStudents,
              }
            )
          : showCloseSubmissionCheckboxWithMsg
          ? t(
              "assessmentEvaluation:mark_count_student_evaluated_and_share_with_close_submission",
              {
                count: selectedStudentsCount,
                totalCount: totalStudents,
              }
            )
          : t(
              "assessmentEvaluation:mark_count_student_evaluated_and_share_message_v2",
              {
                count: selectedStudentsCount,
              }
            );

      confirmButtonText = showCloseSubmissionCheckboxWithMsg
        ? t("common:mark_and_share")
        : t("common:mark_as_label", { label });

      if (allNonEvaluatedStudentsSelected && allowMovingTaskToReviewed) {
        messageSuffix = t("classRoom:should_move_task_to_reviewed_tab");
        inputFields.push({
          key: "moveToReviewed",
          label: t("classRoom:move_task_to_reviewed_tab"),
          uiType: "CHECKBOX",
        });
      }

      if (!_.isEmpty(messageSuffix) && !showCloseSubmissionCheckboxWithMsg) {
        message = `${message} ${messageSuffix}`;
      }

      if (showCloseSubmissionCheckboxWithMsg) {
        inputFields.push({
          key: "closeSubmissions",
          label: t(
            "assessmentEvaluation:close_submission_selected_student_action",
            {
              count: selectedStudentsCount,
            }
          ),
          uiType: "CHECKBOX",
        });
      }

      break;
    }

    case "SHARE": {
      const label = t("common:share");

      if (selectedStudentsCount > 1) {
        title = t(
          "assessmentEvaluation:label_evaluation_with_students_and_families",
          {
            label,
          }
        );
      } else {
        title = t(
          "assessmentEvaluation:label_evaluation_with_student_and_family",
          {
            label,
          }
        );
      }

      message = showCloseSubmissionCheckboxWithMsg
        ? t(
            "assessmentEvaluation:share_evaluation_with_close_submission_for_selected_students_body",
            {
              count: notSharedWithStudentsCount,
              totalCount: totalStudents,
            }
          )
        : t(
            "assessmentEvaluation:share_evaluation_for_selected_students_body",
            {
              count: notSharedWithStudentsCount,
              totalCount: totalStudents,
            }
          );

      confirmButtonText = t("common:share_with_label", {
        label: t("common:evaluation"),
      });

      if (showCloseSubmissionCheckboxWithMsg) {
        inputFields.push({
          key: "closeSubmissions",
          label: t(
            "assessmentEvaluation:close_submission_selected_student_action",
            {
              count: selectedStudentsCount,
            }
          ),
          uiType: "CHECKBOX",
        });
      }

      break;
    }

    case "RETURN": {
      title = t("common:return_submissions");
      message = t("common:remark");
      confirmButtonText = t("common:return");
      inputFields.push({
        key: "remark",
        label: t("common:enter_remark_for_selected_students"),
        uiType: "TEXTAREA",
      });
      break;
    }
  }

  return {
    title,
    message,
    inputFields,
    alertText: getDialogAlertText({
      t,
      dialogAction,
      isStudentSubmissionEnabled,
      notSubmittedCount,
      draftSubmissionsCount,
      isPrivate,
    }),
    confirmButtonText,
  };
};

/**
 * This function handles bulk actions that are performed for all students assigned in an assignment.
 * Also it will handle actions that are performed on an Assignment like delete assignment, share private assignment with students.
 * StudentAssignment level actions - EVALUATE, EVALUATE_SHARE, SHARE, RETURN
 * Assignment level actions - DELETE, SHARE_WITH_STUDENTS
 */
const getBulkActionDialogInfo = ({
  t,
  dialogAction,
  isTeacherEvaluationEnabled,
  isStudentSubmissionEnabled,
  isPrivate,
  notSubmittedCount,
  draftSubmissionsCount,
  notSharedWithStudentsCount,
  assessmentTypeLabel,
  allowMovingTaskToReviewed = false,
  assignmentTitle,
  courseTitle,
  allLinkedClasses,
  isIncludedInCourseFlow = false,
  showCloseSubmissionCheckbox,
  isSubmissionLocked,
}) => {
  let title = null;
  let message = null;
  let messageNode = null;
  const inputFields = [];
  let confirmButtonText = null;
  let deleteAlertText = null;

  const showCloseSubmissionCheckboxWithMsg =
    showCloseSubmissionCheckbox &&
    !isSubmissionLocked &&
    isTeacherEvaluationEnabled;

  const isEnabledRecycleBinForAssignment = getIsEnabledRecycleBinForAssignment();

  switch (dialogAction) {
    case "EVALUATE": {
      const label = isTeacherEvaluationEnabled
        ? t("classRoom:evaluated")
        : t("classRoom:completed");

      title = t("assessmentEvaluation:mark_all_students_as_label", {
        label,
      });

      let messageSuffix = "";
      message =
        showCloseSubmissionCheckboxWithMsg && allowMovingTaskToReviewed
          ? t(
              "assessmentEvaluation:mark_all_students_evaluated_with_close_submission_and_reviewed",
              {
                label,
              }
            )
          : showCloseSubmissionCheckboxWithMsg
          ? t(
              "assessmentEvaluation:mark_all_students_evaluated_with_close_submission",
              {
                label,
              }
            )
          : t("assessmentEvaluation:mark_all_students_evaluated_message_v2", {
              label,
            });

      if (allowMovingTaskToReviewed) {
        messageSuffix = t("classRoom:should_move_task_to_reviewed_tab");
        inputFields.push({
          key: "moveToReviewed",
          label: t("classRoom:move_task_to_reviewed_tab"),
          uiType: "CHECKBOX",
        });
      }

      if (!_.isEmpty(messageSuffix) && !showCloseSubmissionCheckboxWithMsg) {
        message = `${message} ${messageSuffix}`;
      }

      confirmButtonText = t("classRoom:mark_all_as_label", { label });

      if (showCloseSubmissionCheckboxWithMsg) {
        inputFields.push({
          key: "closeSubmissions",
          label: t("assessmentEvaluation:close_submission_bulk_action"),
          uiType: "CHECKBOX",
        });
      }

      break;
    }

    case "EVALUATE_SHARE": {
      const label = t("common:entity1_and_entity2", {
        entity1: t("classRoom:evaluated"),
        entity2: t("common:share"),
      });

      title = t("assessmentEvaluation:mark_all_students_as_label", {
        label,
      });

      let messageSuffix = "";
      message =
        showCloseSubmissionCheckboxWithMsg && allowMovingTaskToReviewed
          ? t(
              "assessmentEvaluation:mark_all_students_evaluated_and_share_with_close_submission_and_reviewed"
            )
          : showCloseSubmissionCheckboxWithMsg
          ? t(
              "assessmentEvaluation:mark_all_students_evaluated_and_share_with_close_submission"
            )
          : t(
              "assessmentEvaluation:mark_all_students_evaluated_and_share_message_v2"
            );

      if (allowMovingTaskToReviewed) {
        messageSuffix = t("classRoom:should_move_task_to_reviewed_tab");
        inputFields.push({
          key: "moveToReviewed",
          label: t("classRoom:move_task_to_reviewed_tab"),
          uiType: "CHECKBOX",
        });
      }

      if (!_.isEmpty(messageSuffix) && !showCloseSubmissionCheckboxWithMsg) {
        message = `${message} ${messageSuffix}`;
      }

      confirmButtonText = showCloseSubmissionCheckboxWithMsg
        ? t("common:mark_and_share")
        : t("classRoom:mark_all_as_label", { label });

      if (showCloseSubmissionCheckboxWithMsg) {
        inputFields.push({
          key: "closeSubmissions",
          label: t("assessmentEvaluation:close_submission_bulk_action"),
          uiType: "CHECKBOX",
        });
      }

      break;
    }

    case "SHARE": {
      title = t("classRoom:share_all_evaluations_with_students_and_families");
      let messageSuffix = "";

      message =
        showCloseSubmissionCheckboxWithMsg && allowMovingTaskToReviewed
          ? t(
              "assessmentEvaluation:share_evaluation_with_close_submission_and_reviewed_for_all_students_body"
            )
          : showCloseSubmissionCheckboxWithMsg
          ? t(
              "assessmentEvaluation:share_evaluation_with_close_submission_for_all_students_body"
            )
          : t("assessmentEvaluation:share_evaluation_for_all_students_body");

      if (allowMovingTaskToReviewed) {
        messageSuffix = t("classRoom:should_move_task_to_reviewed_tab");
        inputFields.push({
          key: "moveToReviewed",
          label: t("classRoom:move_task_to_reviewed_tab"),
          uiType: "CHECKBOX",
        });
      }

      if (!_.isEmpty(messageSuffix) && !showCloseSubmissionCheckboxWithMsg) {
        message = `${message} ${messageSuffix}`;
      }

      confirmButtonText = t("classRoom:share_all_evaluations");

      if (showCloseSubmissionCheckboxWithMsg) {
        inputFields.push({
          key: "closeSubmissions",
          label: t("assessmentEvaluation:close_submission_bulk_action"),
          uiType: "CHECKBOX",
        });
      }

      break;
    }

    case "RETURN": {
      title = t("common:return_submissions");
      message = t("common:remark");
      confirmButtonText = t("common:return");
      inputFields.push({
        key: "remark",
        label: t("common:enter_remark_for_selected_students"),
        uiType: "TEXTAREA",
      });
      break;
    }

    case "DELETE": {
      const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

      if (isLearningCoursesEnabled) {
        title = t("common:delete_with_quotation_label", {
          label: assignmentTitle,
        });

        const assignedClasses = allLinkedClasses || [];
        const classCount = _.size(assignedClasses);
        const isSingleClass = classCount <= 1;
        const effectiveCourseTitle = isSingleClass
          ? _.get(_.first(assignedClasses), "title", courseTitle)
          : t("common:class_count_lowercase", { count: classCount });

        const classesTooltipContent =
          classCount > 1 ? (
            <ul className={classes.classesTooltipList}>
              {_.map(assignedClasses, assignedClass => (
                <li key={assignedClass.id}>{assignedClass.title}</li>
              ))}
            </ul>
          ) : null;

        const ClassCountWithTooltip = ({ children }) => (
          <Tooltip tooltip={classesTooltipContent} placement="top" showArrow>
            <span className={classNames("text-label", classes.classCountLink)}>
              {children}
            </span>
          </Tooltip>
        );

        messageNode = (
          <Trans
            i18nKey="course:delete_item_assigned_to_classes"
            values={{
              itemType: assessmentTypeLabel,
              classCount: effectiveCourseTitle,
            }}
            components={{
              1: isSingleClass ? (
                <span className="text-label" />
              ) : (
                <ClassCountWithTooltip />
              ),
            }}
          />
        );
        if (isIncludedInCourseFlow) {
          deleteAlertText = isSingleClass
            ? t("course:delete_item_warning_with_class", {
                class: effectiveCourseTitle,
              })
            : t("course:delete_item_warning_with_classes");
        }
        confirmButtonText = t("common:delete");
      } else if (isEnabledRecycleBinForAssignment) {
        title = t("common:delete_with_label", { label: assessmentTypeLabel });
        message = t(
          "classRoom:delete_assignment_message_with_label_and_title",
          {
            label: assessmentTypeLabel,
            title: assignmentTitle,
          }
        );
        confirmButtonText = t("common:delete");
      } else {
        title = t("common:delete_with_label", { label: assessmentTypeLabel });
        message = t("classRoom:delete_assignment_message_with_label", {
          label: assessmentTypeLabel,
        });
        confirmButtonText = t("common:delete_agree");
      }

      break;
    }

    case "SHARE_WITH_STUDENTS": {
      title = t("classRoom:show_post_to_student_and_families");
      message = t("classRoom:show_post_to_student_and_families_message");
      confirmButtonText = t("classRoom:show_post_to_student_and_families");
    }
  }

  return {
    title,
    message,
    messageNode,
    inputFields,
    alertText:
      deleteAlertText ??
      getDialogAlertText({
        t,
        dialogAction,
        isStudentSubmissionEnabled,
        notSubmittedCount,
        draftSubmissionsCount,
        isPrivate,
      }),
    confirmButtonText,
  };
};

/**
 * !isBulk : action for single student (in AllSubmissions, MediaManager)
 * isBulk && !areAllStudentsSelected : action for selected students (in AllSubmissions)
 * isBulk && areAllStudentsSelected : action for all students (bulk action in FeedCard, AllSubmissions, Gradebook)
 */
const getDialogInfo = params => {
  const { isBulk, selectedStudentsCount, totalStudents } = params;

  const areAllStudentsSelected = _.includes(
    [0, totalStudents],
    selectedStudentsCount
  );

  if (!isBulk) {
    return getSingleStudentActionDialogInfo(params);
  }

  if (!areAllStudentsSelected) {
    return getSelectedStudentsActionDialogInfo(params);
  }

  return getBulkActionDialogInfo(params);
};

const AssignmentActionDialog = props => {
  const {
    handleConfirmClick,
    handleCancelClick,
    viewType,
    testId = _.camelCase(viewType) ?? "appComponents",
    isLTIAttachmentPresent = false,
    dialogAction,
    pendingAIEvaluation,
    isWorksheetOrAiTutor,
    isStudentSubmissionEnabled,
    isPrivate,
    isBulk,
    isSubmitted,
    isTeacherEvaluationEnabled,
    isSubmissionLocked,
  } = props;

  const showCloseSubmissionCheckbox =
    !isWorksheetOrAiTutor && isStudentSubmissionEnabled && !isPrivate;

  const { t } = useI18n();
  const dialogInfo = getDialogInfo({
    ...props,
    t,
    showCloseSubmissionCheckbox,
  });
  const {
    title,
    message,
    messageNode,
    alertText,
    inputFields,
    confirmButtonText,
  } = dialogInfo || {};

  const isMoveReviewedCheckBoxVisible = _.some(inputFields, {
    key: "moveToReviewed",
  });

  const [inputData, setInputData] = useState({
    closeSubmissions:
      isSubmissionLocked ||
      isBulk ||
      isSubmitted ||
      !isTeacherEvaluationEnabled,
    moveToReviewed: isMoveReviewedCheckBoxVisible,
    remark: "",
  });
  const isGlobalHeaderEnabled = isGlobalHeaderOnMultipleScreensEnabled();

  const onConfirmClick = e => {
    e?.stopPropagation();
    handleConfirmClick(inputData);
  };

  const onCancelClick = e => {
    e?.stopPropagation();
    handleCancelClick();
  };

  const renderInputComponent = field => {
    const { key, label, uiType } = field || {};
    const value = _.get(inputData, key, null);

    const handleCheckboxClick = () => {
      setInputData(prevState => ({
        ...prevState,
        [key]: !prevState[key],
      }));
    };

    const handleTextAreaInput = value => {
      setInputData(prevState => ({ ...prevState, [key]: value }));
    };

    switch (uiType) {
      case "CHECKBOX":
        return (
          <Checkbox
            key={key}
            isChecked={value}
            onChange={handleCheckboxClick}
            testId={`${testId}-assignmentActionDialog-${_.camelCase(key)}`}
            labelClassName={classes.checkboxLabel}
          >
            {label}
          </Checkbox>
        );

      case "TEXTAREA":
        return (
          <FormTextAreaInput
            key={key}
            placeholder={label}
            value={value}
            testId={`${testId}-assignmentActionDialog-${_.camelCase(key)}`}
            updateInputField={handleTextAreaInput}
          />
        );
    }
  };

  const renderInputFields = () => {
    return !_.isEmpty(inputFields) ? (
      <>{_.map(inputFields, field => renderInputComponent(field))}</>
    ) : null;
  };

  const renderPendingAIEvaluationAlert = () => {
    return (
      <Modal
        width="480px"
        height="fit-content"
        isOpen={pendingAIEvaluation}
        onClose={onCancelClick}
        dsVersion="2.0"
      >
        <Modal.Header
          title={t("ai:ai_grading_warning_title")}
          isClosable={true}
        />

        <Modal.Body>{t("ai:ai_grading_submission_warning_message")}</Modal.Body>

        <Modal.Footer>
          <div className={classes.footerContainer}>
            <Button
              dsVersion="2.0"
              size="medium"
              variant="neutral"
              type="outlined"
              onClick={onCancelClick}
            >
              {t("common:keep_editing")}
            </Button>
            <Button
              dsVersion="2.0"
              size="medium"
              variant="progressive"
              onClick={onConfirmClick}
            >
              {t("classRoom:approve_evaluation_and_proceed")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderDialogBox = () => {
    return (
      <Modal
        width="520px"
        height={"max-content"}
        hasOverlay={true}
        shouldCloseOnOverlayClick={true}
        onClose={onCancelClick}
      >
        <Modal.Header title={title} isClosable={true} />
        <Modal.Body>
          <div className={classes.bodyContainer}>
            <div className={classes.innerContainer}>
              {isLTIAttachmentPresent &&
              _.isEqual(dialogAction, "RETURN", false) ? (
                <Alert
                  dsVersion="2.0"
                  type="warning"
                  message={t("classRoom:resubmission_modal_lti_tool_warning")}
                  style={styles.alertContainerStyle}
                />
              ) : null}
              {messageNode ? (
                <div
                  className={classNames("text-body", classes.dialogueMessage)}
                >
                  {messageNode}
                </div>
              ) : null}
              {!messageNode && !_.isEmpty(message) ? (
                <div
                  className={classNames("text-body-1", classes.dialogueMessage)}
                  dangerouslySetInnerHTML={{
                    __html: getStyleStrippedText({
                      text: message,
                      type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                    }),
                  }}
                />
              ) : null}
              {renderInputFields()}
            </div>
            {!_.isEmpty(alertText) ? (
              <Alert type={"warning"} message={alertText} />
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.buttonContainer}>
            <Button
              onClick={onCancelClick}
              size="large"
              dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
              variant={isGlobalHeaderEnabled ? "neutral" : "outlined-subtle"}
              testId={`${testId}-assignmentActionDialog-cancel`}
              type="outlined"
            >
              {t("common:cancel")}
            </Button>
            <Button
              onClick={onConfirmClick}
              size="large"
              dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
              testId={`${testId}-assignmentActionDialog-confirm`}
              shortcut={{ code: "Enter", shiftKey: true }}
            >
              {confirmButtonText}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  return pendingAIEvaluation
    ? renderPendingAIEvaluationAlert()
    : !_.isEmpty(dialogInfo)
    ? renderDialogBox()
    : null;
};

AssignmentActionDialog.propTypes = {
  dialogAction: PropTypes.string,
  isBulk: PropTypes.bool,
  allowMovingTaskToReviewed: PropTypes.bool,
  isTeacherEvaluationEnabled: PropTypes.bool,
  isStudentSubmissionEnabled: PropTypes.bool,
  isPrivate: PropTypes.bool,
  selectedStudentsCount: PropTypes.number,
  totalStudents: PropTypes.number,
  notSubmittedCount: PropTypes.number,
  draftSubmissionsCount: PropTypes.number,
  openSubmissionsCount: PropTypes.number,
  notEvaluatedCount: PropTypes.number,
  notSharedWithStudentsCount: PropTypes.number,
  studentName: PropTypes.string,
  assessmentTypeLabel: PropTypes.string,
  handleConfirmClick: PropTypes.func,
  handleCancelClick: PropTypes.func,
};

AssignmentActionDialog.defaultProps = {
  isBulk: true,
  allowMovingTaskToReviewed: false,
  selectedStudentsCount: 0,
  openSubmissionsCount: 0,
};

export default AssignmentActionDialog;
